import { useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { fetchBusiness, createBusiness, updateBusiness, saveBusinessHours, saveBusinessServices, saveBusinessSocialLinks, saveBusinessLanguages, saveBusinessPaymentMethods, fetchEmirates, fetchServices, fetchLanguages, fetchPaymentMethods } from '@/lib/api'
import type { Business, Emirate, Service, Language, PaymentMethod, BusinessHours, BusinessSocialLink } from '@/lib/types'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const PLATFORMS = ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'youtube'] as const
const PLAN_OPTIONS = ['free', 'basic', 'premium'] as const

const emptyBusiness: Omit<Business, 'id' | 'created_at' | 'updated_at' | 'emirate'> = {
  name_en: '', name_ar: '', slug: '', emirate_id: '', category_id: null,
  address_en: null, address_ar: null, makani: null, phone: null, whatsapp: null,
  email: null, website: null, description_en: null, description_ar: null,
  rating: null, review_count: 0, is_verified: false, is_active: true,
  plan: 'free', latitude: null, longitude: null,
}

export default function BusinessForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [form, setForm] = useState(emptyBusiness)
  const [hours, setHours] = useState<BusinessHours[]>(DAYS.map((_, i) => ({ day_of_week: i, open_time: null, close_time: null, is_closed: true })))
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [availableServices, setAvailableServices] = useState<Service[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([])
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([])
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>([])
  const [socialLinks, setSocialLinks] = useState<Omit<BusinessSocialLink, 'id' | 'business_id'>[]>([])
  const [emirates, setEmirates] = useState<Emirate[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  useEffect(() => { fetchEmirates().then(setEmirates); fetchServices().then(setAvailableServices); fetchLanguages().then(setAvailableLanguages); fetchPaymentMethods().then(setAvailablePaymentMethods) }, [])

  useEffect(() => {
    if (isNew) return
    setLoading(true)
    fetchBusiness(id!).then(b => {
      setForm({
        name_en: b.name_en, name_ar: b.name_ar, slug: b.slug, emirate_id: b.emirate_id, category_id: b.category_id,
        address_en: b.address_en, address_ar: b.address_ar, makani: b.makani, phone: b.phone, whatsapp: b.whatsapp,
        email: b.email, website: b.website, description_en: b.description_en, description_ar: b.description_ar,
        rating: b.rating, review_count: b.review_count, is_verified: b.is_verified, is_active: b.is_active,
        plan: b.plan, latitude: b.latitude, longitude: b.longitude,
      })
      if (b.business_hours?.length) setHours(DAYS.map((_, i) => b.business_hours!.find(h => h.day_of_week === i) ?? { day_of_week: i, open_time: null, close_time: null, is_closed: true }))
      if (b.business_services?.length) {
        const withIds = b.business_services.filter(s => s.service_id).map(s => s.service_id!)
        if (withIds.length > 0) setSelectedServices(withIds)
      }
      if (b.business_languages?.length) {
        const withIds = b.business_languages.filter(l => l.language_id).map(l => l.language_id!)
        if (withIds.length > 0) setSelectedLanguages(withIds)
      }
      if (b.business_payment_methods?.length) {
        const withIds = b.business_payment_methods.filter(m => m.method_id).map(m => m.method_id!)
        if (withIds.length > 0) setSelectedPaymentMethods(withIds)
      }
      if (b.business_social_links?.length) setSocialLinks(b.business_social_links.map(l => ({ platform: l.platform, url: l.url })))
      setLoading(false)
    }).catch(err => { setError(err.message); setLoading(false) })
  }, [id, isNew])

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) { setForm(prev => ({ ...prev, [key]: value })) }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault(); setSaving(true); setError('')
    try {
      let businessId = id!
      if (isNew) businessId = await createBusiness(form)
      else await updateBusiness(businessId, form)
      await saveBusinessHours(businessId, hours)
      await saveBusinessServices(businessId, selectedServices.map(sid => { const svc = availableServices.find(s => s.id === sid); return { name_en: svc?.name_en ?? '', service_id: sid } }))
      await saveBusinessLanguages(businessId, selectedLanguages.map(lid => { const lang = availableLanguages.find(l => l.id === lid); return { language: lang?.name ?? '', language_id: lid } }))
      await saveBusinessPaymentMethods(businessId, selectedPaymentMethods.map(pid => { const pm = availablePaymentMethods.find(p => p.id === pid); return { method: pm?.name ?? '', method_id: pid } }))
      await saveBusinessSocialLinks(businessId, socialLinks.filter(l => l.url.trim()))
      navigate('/businesses')
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Save failed') }
    finally { setSaving(false) }
  }

  function toggle(section: string) { setCollapsed(prev => ({ ...prev, [section]: !prev[section] })) }

  function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
    const isOpen = !collapsed[id]
    return (
      <section className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden transition-all duration-200">
        <button type="button" onClick={() => toggle(id)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-surface-50/50 transition-colors">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">{title}</h2>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
        </button>
        {isOpen && <div className="px-6 pb-6">{children}</div>}
      </section>
    )
  }

  if (loading) {
    return <Layout title="Loading..."><div className="flex items-center justify-center py-20"><div className="text-slate-500">Loading business data...</div></div></Layout>
  }

  return (
    <Layout title={isNew ? 'New Business' : 'Edit Business'}
      action={
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/businesses')} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-4 py-2 rounded-xl border border-surface-200 hover:bg-surface-50 transition-colors">Cancel</button>
          <button type="submit" form="business-form" disabled={saving} className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-all shadow-lg shadow-brand-500/20">
            {saving ? 'Saving...' : 'Save Business'}
          </button>
        </div>
      }
    >
      <div className="max-w-4xl mx-auto space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-5 py-4 font-medium">{error}</div>}

        <form id="business-form" onSubmit={handleSubmit} className="space-y-4">
          <Section id="basic" title="Basic Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Name (EN)" required value={form.name_en} onChange={v => update('name_en', v)} />
              <F label="Name (AR)" value={form.name_ar ?? ''} onChange={v => update('name_ar', v || null)} dir="rtl" />
              <F label="Slug" required value={form.slug} onChange={v => update('slug', v)} />
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Emirate *</label>
                <select required value={form.emirate_id} onChange={e => update('emirate_id', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                  <option value="">Select...</option>
                  {emirates.map(e => <option key={e.id} value={e.id}>{e.name_en}</option>)}
                </select>
              </div>
              <F label="Phone" value={form.phone ?? ''} onChange={v => update('phone', v || null)} />
              <F label="WhatsApp" value={form.whatsapp ?? ''} onChange={v => update('whatsapp', v || null)} />
              <F label="Email" type="email" value={form.email ?? ''} onChange={v => update('email', v || null)} />
              <F label="Website" value={form.website ?? ''} onChange={v => update('website', v || null)} />
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Address (EN)</label>
                <input className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" value={form.address_en ?? ''} onChange={e => update('address_en', e.target.value || null)} />
              </div>
              <F label="Makani Number" value={form.makani ?? ''} onChange={v => update('makani', v || null)} />
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description (EN)</label>
                <textarea rows={4} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none" value={form.description_en ?? ''} onChange={e => update('description_en', e.target.value || null)} />
              </div>
            </div>
          </Section>

          <Section id="location" title="Location & Rating">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <F label="Latitude" type="number" value={form.latitude?.toString() ?? ''} onChange={v => update('latitude', v ? parseFloat(v) : null)} />
              <F label="Longitude" type="number" value={form.longitude?.toString() ?? ''} onChange={v => update('longitude', v ? parseFloat(v) : null)} />
              <F label="Rating (0-5)" type="number" min="0" max="5" step="0.1" value={form.rating?.toString() ?? ''} onChange={v => update('rating', v ? parseFloat(v) : null)} />
              <F label="Review Count" type="number" value={form.review_count.toString()} onChange={v => update('review_count', parseInt(v) || 0)} />
            </div>
          </Section>

          <Section id="settings" title="Settings">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Plan</label>
                <select value={form.plan} onChange={e => update('plan', e.target.value as typeof form.plan)} className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                  {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2.5 gap-6">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={form.is_active} onChange={e => update('is_active', e.target.checked)} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm font-medium text-slate-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={form.is_verified} onChange={e => update('is_verified', e.target.checked)} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm font-medium text-slate-700">Verified</span>
                </label>
              </div>
            </div>
          </Section>

          <Section id="hours" title="Opening Hours">
            <div className="space-y-1.5">
              {DAYS.map((day, i) => {
                const h = hours[i]
                return (
                  <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${!h.is_closed ? 'bg-brand-50/50' : 'bg-surface-50'}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!h.is_closed} onChange={() => { const next = [...hours]; next[i] = { ...next[i], is_closed: !h.is_closed }; setHours(next) }} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                    </label>
                    <span className={`w-28 text-sm font-medium ${h.is_closed ? 'text-slate-400' : 'text-slate-700'}`}>{day}</span>
                    {!h.is_closed ? (
                      <>
                        <input type="time" value={h.open_time?.slice(0, 5) ?? ''} onChange={e => { const next = [...hours]; next[i] = { ...next[i], open_time: e.target.value ? e.target.value + ':00' : null }; setHours(next) }} className="px-3 py-1.5 rounded-lg border border-surface-200 text-sm w-36 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                        <span className="text-slate-400 text-sm">to</span>
                        <input type="time" value={h.close_time?.slice(0, 5) ?? ''} onChange={e => { const next = [...hours]; next[i] = { ...next[i], close_time: e.target.value ? e.target.value + ':00' : null }; setHours(next) }} className="px-3 py-1.5 rounded-lg border border-surface-200 text-sm w-36 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                      </>
                    ) : <span className="text-sm text-slate-400">Closed</span>}
                  </div>
                )
              })}
            </div>
          </Section>

          <Section id="services" title="Services">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availableServices.map(svc => {
                const checked = selectedServices.includes(svc.id)
                return (
                  <label key={svc.id} className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 text-sm ${checked ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm' : 'border-surface-200 text-slate-600 hover:bg-surface-50 hover:border-surface-300'}`}>
                    <input type="checkbox" checked={checked} onChange={() => setSelectedServices(prev => prev.includes(svc.id) ? prev.filter(id => id !== svc.id) : [...prev, svc.id])} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                    {svc.name_en}
                  </label>
                )
              })}
            </div>
          </Section>

          <Section id="languages" title="Languages">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {availableLanguages.map(lang => {
                const checked = selectedLanguages.includes(lang.id)
                return (
                  <label key={lang.id} className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 text-sm ${checked ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm' : 'border-surface-200 text-slate-600 hover:bg-surface-50 hover:border-surface-300'}`}>
                    <input type="checkbox" checked={checked} onChange={() => setSelectedLanguages(prev => prev.includes(lang.id) ? prev.filter(id => id !== lang.id) : [...prev, lang.id])} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                    {lang.name}
                  </label>
                )
              })}
            </div>
          </Section>

          <Section id="payments" title="Payment Methods">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availablePaymentMethods.map(pm => {
                const checked = selectedPaymentMethods.includes(pm.id)
                return (
                  <label key={pm.id} className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 text-sm ${checked ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm' : 'border-surface-200 text-slate-600 hover:bg-surface-50 hover:border-surface-300'}`}>
                    <input type="checkbox" checked={checked} onChange={() => setSelectedPaymentMethods(prev => prev.includes(pm.id) ? prev.filter(id => id !== pm.id) : [...prev, pm.id])} className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                    {pm.name}
                  </label>
                )
              })}
            </div>
          </Section>

          <Section id="social" title="Social Links">
            <div className="space-y-3">
              {socialLinks.map((link, i) => (
                <div key={i} className="flex items-center gap-3">
                  <select value={link.platform} onChange={e => { const next = [...socialLinks]; next[i] = { ...next[i], platform: e.target.value }; setSocialLinks(next) }} className="px-3 py-2.5 rounded-xl border border-surface-200 text-sm bg-white w-36 focus:outline-none focus:ring-2 focus:ring-brand-500/20">
                    {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input type="url" value={link.url} onChange={e => { const next = [...socialLinks]; next[i] = { ...next[i], url: e.target.value }; setSocialLinks(next) }} placeholder="https://..." className="flex-1 px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                  <button type="button" onClick={() => setSocialLinks(prev => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500 transition-colors p-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setSocialLinks(prev => [...prev, { platform: 'instagram', url: '' }])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add link
              </button>
            </div>
          </Section>
        </form>
      </div>
    </Layout>
  )
}

type FieldProps = { label: string; required?: boolean; value: string; onChange: (value: string) => void; type?: string; dir?: string; className?: string; placeholder?: string; min?: string; max?: string; step?: string }
function F({ label, required, onChange, className, ...inputProps }: FieldProps) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label} {required && '*'}</label>
      <input {...inputProps} onChange={e => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all ${className ?? ''}`}
      />
    </div>
  )
}
